import { useState } from 'react';
import { motion } from 'framer-motion';
import { Building2, Mail, MapPin, Phone, Trash2, Edit2, X, Check } from 'lucide-react';
import { useToast } from '../hooks/useToast';
import ConfirmDeleteModal from './ui/ConfirmDeleteModal';

function getInitial(name) {
  const n = (name || '').trim();
  return n ? n[0].toUpperCase() : '?';
}

function EmployeeCard({ employee, isNew = false, onDelete, onUpdate }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(employee?.name || '');
  const [editedEmail, setEditedEmail] = useState(employee?.email || '');
  const [editedPhone, setEditedPhone] = useState(employee?.phone || '');
  const [editedCompany, setEditedCompany] = useState(employee?.company?.name || '');
  const [editedCity, setEditedCity] = useState(employee?.address?.city || '');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  const { showSuccess, showError } = useToast();

  const name = employee?.name ?? 'Unknown';
  const username = employee?.username ?? name.toLowerCase().replace(/\s+/g, '');
  const email = employee?.email ?? '-';
  const phone = employee?.phone ?? '-';
  const companyName = employee?.company?.name ?? '-';
  const city = employee?.address?.city ?? '-';

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete?.(employee.id);
      // Toast is handled in App.js
    } catch (error) {
      showError(`Failed to delete ${name}`);
      setIsDeleting(false);
    }
  };

  const handleUpdate = async () => {
    if (!editedName || !editedEmail || !editedPhone || !editedCompany || !editedCity) {
      showError('All fields are required');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editedEmail)) {
      showError('Please enter a valid email address');
      return;
    }

    try {
      await onUpdate?.(employee.id, {
        name: editedName.trim(),
        email: editedEmail.trim(),
        phone: editedPhone.trim(),
        company: { name: editedCompany.trim() },
        address: { city: editedCity.trim() },
      });
      setIsEditing(false);
      // Toast is handled in App.js
    } catch (error) {
      showError(`Failed to update ${name}`);
    }
  };

  const handleCancelEdit = () => {
    setEditedName(employee?.name || '');
    setEditedEmail(employee?.email || '');
    setEditedPhone(employee?.phone || '');
    setEditedCompany(employee?.company?.name || '');
    setEditedCity(employee?.address?.city || '');
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border-2 border-blue-500 dark:border-blue-400"
      >
        <div className="space-y-3">
          <input
            type="text"
            value={editedName}
            onChange={(e) => setEditedName(e.target.value)}
            className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Name"
          />
          <input
            type="email"
            value={editedEmail}
            onChange={(e) => setEditedEmail(e.target.value)}
            className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Email"
          />
          <input
            type="tel"
            value={editedPhone}
            onChange={(e) => setEditedPhone(e.target.value)}
            className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Phone"
          />
          <input
            type="text"
            value={editedCompany}
            onChange={(e) => setEditedCompany(e.target.value)}
            className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Company"
          />
          <input
            type="text"
            value={editedCity}
            onChange={(e) => setEditedCity(e.target.value)}
            className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="City"
          />
          <div className="flex gap-2 pt-2">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleUpdate}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              <Check size={16} />
              Save
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleCancelEdit}
              className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors flex items-center justify-center gap-2"
            >
              <X size={16} />
              Cancel
            </motion.button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={isNew ? { opacity: 0, y: 10, scale: 0.98 } : { opacity: 1 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.22, ease: 'easeOut' }}
      whileHover={{ y: -4, transition: { duration: 0.15 } }}
      className={[
        'bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700',
        'hover:shadow-lg transition-shadow',
        isNew ? 'animate-card-in' : '',
        isDeleting ? 'opacity-50' : '',
      ].join(' ')}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1 truncate">
            {name}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
            @{username}
          </p>
        </div>

        <div className="shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg">
          {getInitial(name)}
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
          <Mail size={16} className="text-gray-400 dark:text-gray-500" />
          <span className="truncate">{email}</span>
        </div>

        <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
          <Phone size={16} className="text-gray-400 dark:text-gray-500" />
          <span className="truncate">{phone}</span>
        </div>

        <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
          <Building2 size={16} className="text-gray-400 dark:text-gray-500" />
          <span className="truncate">{companyName}</span>
        </div>

        <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
          <MapPin size={16} className="text-gray-400 dark:text-gray-500" />
          <span className="truncate">{city}</span>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
          ID: {employee?.id ?? '-'}
        </span>

        {(onDelete || onUpdate) && (
          <div className="flex gap-2">
            {onUpdate && (
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setIsEditing(true)}
                className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                title="Edit employee"
              >
                <Edit2 size={16} />
              </motion.button>
            )}
            {onDelete && (
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={handleDeleteClick}
                disabled={isDeleting}
                className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors disabled:opacity-50"
                title="Delete employee"
              >
                <Trash2 size={16} />
              </motion.button>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        title={`Delete ${name}?`}
        message="This action cannot be undone."
      />
    </motion.div>
  );
}

export default EmployeeCard;
